import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';

const BudgetForm = ({ data, allData, onSubmitForm }) => {
  const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm();
  const [selectedCoas, setSelectedCoas] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const invoiceValue = watch('invoice');

  

  // Filter COAs based on selected invoice
  const filteredCoas = data
    .filter(item => item.type == 1 && item.invoice === invoiceValue)
    .map(item => ({
      coa: item.coa,
      label: allData.budget?.find(a => a.id === item.coa)?.coa || 'Unknown COA'
    }));



  // Handle checkbox changes
  const handleCoaChange = (coaId, isChecked) => {
    if (isChecked) {
      setSelectedCoas(prev => [...prev, { coa: coaId, amount: data
        .filter(item => item.type == 1 && item.invoice === invoiceValue && item.coa === coaId)[0]?.amount }]);
    } else {
      setSelectedCoas(prev => prev.filter(item => item.coa !== coaId));
    }
  };

  // Handle amount changes
  const handleAmountChange = (coaId, value) => {
    setSelectedCoas(prev =>
      prev.map(item =>
        item.coa === coaId ? { ...item, amount: Number(value) } : item
      )
    );
  };

  // Update form values when selectedCoas changes
  useEffect(() => {
    setValue('coa', selectedCoas);
  }, [selectedCoas, setValue]);

  // Clear COAs when invoice changes
  useEffect(() => {
    setSelectedCoas([]);
  }, [invoiceValue]);

  const onSubmit = async data => {
    // Validate amounts
    if (data.coa.some(item => item.amount <= 0)) {
      toast.error('All COAs must have a positive amount');
      return;
    }


    setIsDisabled(true)
    await onSubmitForm(data)
    
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl mx-auto p-4 space-y-6 ">
        <div className='grid grid-cols-2 gap-4'>
      {/* Invoice Field */}
      <div >
        <label className="block text-sm font-medium text-gray-700">Invoice</label>
        <select
        required
          {...register('invoice', { required: true })}
          className="w-full p-1 border rounded-md text-lg font-normal focus:outline-none focus:border-blue-950"
        >
          <option value="">Select Invoice</option>
          {[...new Set(data.filter(a => a.type == 1).map(a => a.invoice))].map(invoice => (
            <option key={invoice} value={invoice}>{invoice}</option>
          ))}
        </select>
        {errors.invoice && <span className="text-red-500 text-sm">This field is required</span>}
      </div>

      {/* COA Checkboxes with Amounts */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Chart of Accounts</label>
        <div className="mt-2 space-y-3">
          {filteredCoas.map((item) => (
            <div key={item.coa} className="flex items-center gap-4">
              <input
                type="checkbox"
                checked={selectedCoas.some(c => c.coa === item.coa)}
                onChange={(e) => handleCoaChange(item.coa, e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="flex-1">
                {item.label}
              </label>
              {selectedCoas.some(c => c.coa === item.coa) && (
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={selectedCoas.find(c => c.coa === item.coa)?.amount || 0}
                  onChange={(e) => handleAmountChange(item.coa, e.target.value)}
                  className="w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Amount"
                />
              )}
            </div>
          ))}
        </div>
        {errors.coa && <span className="text-red-500 text-sm">At least one COA must be selected</span>}
      </div>

      {/* Other form fields (keep the same as previous implementation) */}
      {/* Source of Fund */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Source of Fund</label>
        <select
        required
          {...register('sof', { required: true })}
          className="w-full p-1 border rounded-md text-lg font-normal focus:outline-none focus:border-blue-950"
        >
          <option value="">Select Source of Fund</option>
          {allData.sof.map(sof => (
            <option key={sof.id} value={sof.id}>{sof.name}</option>
          ))}
        </select>
        {errors.sof && <span className="text-red-500 text-sm">This field is required</span>}
      </div>

      {/* Bank */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Bank</label>
        <select
        required
          {...register('bank', { required: true })}
          className="w-full p-1 border rounded-md text-lg font-normal focus:outline-none focus:border-blue-950"
        >
          <option value="">Select Bank</option>
          {allData.bank.map(bank => (
            <option key={bank.id} value={bank.id}>{bank.name}</option>
          ))}
        </select>
        {errors.bank && <span className="text-red-500 text-sm">This field is required</span>}
      </div>

      {/* Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Type</label>
        <select
        required
          {...register('type', { required: true })}
          className="w-full p-1 border rounded-md text-lg font-normal focus:outline-none focus:border-blue-950"
        >
          <option value="">Select Type</option>
          <option value="1">Payment</option>
          <option value="2">Credit note</option>
        </select>
        {errors.type && <span className="text-red-500 text-sm">This field is required</span>}
      </div>

      {/* Cheque Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Cheque #</label>
        <input
        required
          type="text"
          {...register('cheque', { required: true })}
          className="w-full p-1 border rounded-md text-lg font-normal focus:outline-none focus:border-blue-950"
        />
        {errors.cheque && <span className="text-red-500 text-sm">This field is required</span>}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Description</label>
        <textarea
        required
          {...register('description', { required: true })}
          className="w-full p-1 border rounded-md text-lg font-normal focus:outline-none focus:border-blue-950"
          rows="3"
        />
        {errors.description && <span className="text-red-500 text-sm">This field is required</span>}
      </div>
      </div>
      <button
      disabled={isDisabled}
        type="submit"
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        Submit
      </button>
      
    </form>
  );
};

export default BudgetForm;